import { readTopicSettings } from '@guildite/domain/notifications';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

import type { AccountSettingsPort } from '../ports.ts';
import type { WorkerClient } from '../worker.ts';
import type { FirebaseServices } from './app.ts';

// The person's own private record (users/{uid}: owner-only by the rules),
// their data export and account deletion.
export function createAccountSettings(services: FirebaseServices, worker: WorkerClient): AccountSettingsPort {
  const uid = () => {
    const current = services.auth.currentUser?.uid;
    if (!current) throw new Error('Sign in to continue.');
    return current;
  };
  return {
    async privateDetails() {
      const [{ get, ref }, database] = await Promise.all([import('firebase/database'), services.database()]);
      const value = ((await get(ref(database, `users/${uid()}`))).val() ?? {}) as Record<string, unknown>;
      const user = services.auth.currentUser;
      return {
        email: user?.email ?? (typeof value.email === 'string' ? value.email : null),
        country: typeof value.country === 'string' ? value.country : 'GH',
        town: typeof value.town === 'string' ? value.town : '',
        signInMethods: user?.providerData.map(provider => provider.providerId) ?? [],
      };
    },
    async savePrivate({ country, town }) {
      const [{ ref, update }, database] = await Promise.all([import('firebase/database'), services.database()]);
      await update(ref(database, `users/${uid()}`), { country, town: town.trim().slice(0, 60) });
    },
    exportData: () => worker.call<Record<string, unknown>>('export_my_data'),
    registerPush: async subscription => { await worker.call('register_push_subscription', { subscription }); },
    unregisterPush: async endpoint => { await worker.call('unregister_push_subscription', { endpoint }); },
    notificationTopics: async () => readTopicSettings((await worker.call<{ topics: unknown }>('get_notification_settings')).topics),
    messageFrom: async () => ((await worker.call<{ from: string }>('get_message_settings')).from === 'following' ? 'following' : 'everyone'),
    setMessageFrom: async from => ((await worker.call<{ from: string }>('set_message_settings', { from })).from === 'following' ? 'following' : 'everyone'),
    readReceipts: async () => (await worker.call<{ readReceipts?: boolean }>('get_message_settings')).readReceipts !== false,
    setReadReceipts: async on => (await worker.call<{ readReceipts?: boolean }>('set_message_settings', { readReceipts: on })).readReceipts !== false,
    setNotificationTopics: async topics => readTopicSettings((await worker.call<{ topics: unknown }>('set_notification_settings', { topics })).topics),
    async deleteAccount(password) {
      const user = services.auth.currentUser;
      if (!user) throw new Error('Sign in to continue.');
      // The Worker accepts deletion only within 5 minutes of signing in.
      if (password && user.email) {
        try {
          await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
        } catch (error) {
          throw new Error('That password is not right.', { cause: error });
        }
      }
      await worker.call('delete_account', { confirm: true });
    },
  };
}
